import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { CommunicationsDashboardData } from '../../../models/MasterAdmin/communications';
import { COMMUNICATIONS_BASE_PATH } from '../../../models/MasterAdmin/communications';
import {
  commCardClass,
  commDesktopTableWrapClass,
  commMobileCardClass,
  commSecondaryBtnClass,
  commSectionDescClass,
  commSectionTitleClass,
  commSelectClass,
  commToolbarClass,
} from '../../../components/Communications/communicationsUi';
import { getCommunicationsDashboard } from '../../../services/MasterAdmin/communicationsService';

const DAYS_OPTIONS = [7, 30, 90];

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function labelize(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const CommunicationsDashboardPanel: React.FC = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<CommunicationsDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getCommunicationsDashboard(days);
      setData(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  const maxDaily = useMemo(
    () => Math.max(...(data?.dailyVolume.map((d) => d.count) || [1]), 1),
    [data?.dailyVolume]
  );

  if (loading && !data) {
    return (
      <div className="flex flex-1 items-center justify-center text-gray-500">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className={commToolbarClass}>
        <label className="flex w-full items-center gap-2 text-sm text-gray-700 dark:text-gray-300 sm:w-auto">
          <span className="shrink-0">Range</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className={commSelectClass}>
            {DAYS_OPTIONS.map((d) => (
              <option key={d} value={d}>
                Last {d} days
              </option>
            ))}
          </select>
        </label>
        <button type="button" onClick={() => void load()} disabled={loading} className={commSecondaryBtnClass}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Refresh
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          {error}
        </div>
      ) : null}

      {data ? (
        <>
          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard label="Total events" value={data.totals.total} />
            <KpiCard label="Outbound calls" value={data.totals.outboundCalls} />
            <KpiCard label="Active now" value={data.activeCalls?.total_active ?? 0} accent="text-green-600" />
            <KpiCard
              label="Avg duration"
              valueText={formatDuration(data.totals.avgDurationSeconds)}
            />
            <KpiCard label="WhatsApp requested" value={data.totals.whatsappRequested} />
            <KpiCard label="IVR digit 1" value={data.totals.digitOne} />
            <KpiCard label="Status callbacks" value={data.totals.statusCallbacks} />
            <KpiCard label="Calls with duration" value={data.metrics.completedWithDuration} />
          </section>

          <section className={commCardClass}>
            <h2 className={commSectionTitleClass}>Setup status</h2>
            <p className={commSectionDescClass}>Configure missing items in Settings before going live.</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <SetupItem ok={data.setup.apiConfigured} label="Exotel API credentials" />
              <SetupItem ok={data.setup.exoPhoneConfigured} label="ExoPhone (Caller ID)" />
              <SetupItem ok={data.setup.ivrEnabled} label="IVR routing enabled" />
            </div>
            {(!data.setup.apiConfigured || !data.setup.exoPhoneConfigured) && (
              <p className="mt-3 text-sm text-amber-700 dark:text-amber-300">
                <Link to={`${COMMUNICATIONS_BASE_PATH}/settings`} className="underline">
                  Go to Settings
                </Link>{' '}
                to add API key, token, Account SID, and ExoPhone.
              </p>
            )}
          </section>

          <div className="grid gap-6 lg:grid-cols-2">
            <section className={commCardClass}>
              <h2 className={commSectionTitleClass}>Events by source</h2>
              <div className="mt-4 space-y-2">
                {Object.entries(data.bySource).map(([key, count]) => (
                  <BarRow key={key} label={labelize(key)} count={count} max={data.totals.total || 1} />
                ))}
              </div>
            </section>

            <section className={commCardClass}>
              <h2 className={commSectionTitleClass}>Events by action</h2>
              <div className="mt-4 space-y-2">
                {Object.entries(data.byAction)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 8)
                  .map(([key, count]) => (
                    <BarRow key={key} label={labelize(key)} count={count} max={data.totals.total || 1} />
                  ))}
              </div>
            </section>
          </div>

          <section className={commCardClass}>
            <h2 className={commSectionTitleClass}>Daily volume</h2>
            <p className={commSectionDescClass}>Webhook and call events received per day.</p>
            <div className="mt-4 flex items-end gap-1 overflow-x-auto pb-2">
              {data.dailyVolume.length === 0 ? (
                <p className="text-sm text-gray-500">No events in this range yet.</p>
              ) : (
                data.dailyVolume.map((day) => (
                  <div key={day.date} className="flex min-w-[2.5rem] flex-col items-center gap-1">
                    <div
                      className="w-8 rounded-t bg-primary-500 dark:bg-primary-400"
                      style={{ height: `${Math.max(8, (day.count / maxDaily) * 96)}px` }}
                      title={`${day.count} events`}
                    />
                    <span className="text-[10px] text-gray-500">{day.date.slice(5)}</span>
                  </div>
                ))
              )}
            </div>
          </section>

          {data.activeCalls?.active_calls && data.activeCalls.active_calls.length > 0 ? (
            <section className={commCardClass}>
              <h2 className={commSectionTitleClass}>Live calls (Exotel API)</h2>
              <div className="mt-4 space-y-3 md:hidden">
                {data.activeCalls.active_calls.map((call) => (
                  <div key={call.call_sid} className={commMobileCardClass}>
                    <p className="text-xs font-medium uppercase text-gray-500">From</p>
                    <p className="break-all text-sm font-medium text-gray-900 dark:text-white">{call.from || '—'}</p>
                    <p className="mt-2 text-xs font-medium uppercase text-gray-500">To</p>
                    <p className="break-all text-sm text-gray-700 dark:text-gray-300">{call.to || '—'}</p>
                    <p className="mt-2 text-xs text-gray-500">
                      {call.status || '—'} · {call.duration != null ? formatDuration(call.duration) : '—'}
                    </p>
                  </div>
                ))}
              </div>
              <div className={`mt-4 ${commDesktopTableWrapClass}`}>
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-gray-500">
                      <th className="pb-2 pr-4">From</th>
                      <th className="pb-2 pr-4">To</th>
                      <th className="pb-2 pr-4">Status</th>
                      <th className="pb-2">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.activeCalls.active_calls.map((call) => (
                      <tr key={call.call_sid} className="border-t border-gray-200 dark:border-gray-700">
                        <td className="py-2 pr-4">{call.from || '—'}</td>
                        <td className="py-2 pr-4">{call.to || '—'}</td>
                        <td className="py-2 pr-4">{call.status || '—'}</td>
                        <td className="py-2">{call.duration != null ? formatDuration(call.duration) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          <section className={commCardClass}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className={commSectionTitleClass}>Recent activity</h2>
                <p className={commSectionDescClass}>Latest events from IVR, outbound calls, and webhooks.</p>
              </div>
              <Link to={`${COMMUNICATIONS_BASE_PATH}/activity`} className={commSecondaryBtnClass}>
                View all
              </Link>
            </div>
            <div className="mt-4 space-y-3 md:hidden">
              {data.recentCalls.length === 0 ? (
                <p className="py-6 text-center text-sm text-gray-500">
                  No recent events. Place a test call or trigger your IVR webhook.
                </p>
              ) : (
                data.recentCalls.map((row) => (
                  <div key={row.id} className={commMobileCardClass}>
                    <p className="break-all text-sm font-medium text-gray-900 dark:text-white">{row.fromNumber || '—'}</p>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                      {labelize(row.actionTaken || row.source || '—')}
                    </p>
                    <p className="mt-2 text-xs text-gray-500">
                      {row.duration != null ? formatDuration(row.duration) : '—'} ·{' '}
                      {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                    </p>
                  </div>
                ))
              )}
            </div>
            <div className={`mt-4 ${commDesktopTableWrapClass}`}>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500">
                    <th className="pb-2 pr-4">From</th>
                    <th className="pb-2 pr-4">Action</th>
                    <th className="pb-2 pr-4">Duration</th>
                    <th className="pb-2">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentCalls.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-6 text-center text-gray-500">
                        No recent events. Place a test call or trigger your IVR webhook.
                      </td>
                    </tr>
                  ) : (
                    data.recentCalls.map((row) => (
                      <tr key={row.id} className="border-t border-gray-200 dark:border-gray-700">
                        <td className="py-2 pr-4">{row.fromNumber || '—'}</td>
                        <td className="py-2 pr-4">{labelize(row.actionTaken || row.source || '—')}</td>
                        <td className="py-2 pr-4">
                          {row.duration != null ? formatDuration(row.duration) : '—'}
                        </td>
                        <td className="py-2 text-xs text-gray-500">
                          {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
};

function KpiCard({
  label,
  value,
  valueText,
  accent,
}: {
  label: string;
  value?: number;
  valueText?: string;
  accent?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-4">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </span>
      <div className={`mt-1 text-xl font-bold tabular-nums text-gray-900 dark:text-white sm:text-2xl ${accent || ''}`}>
        {valueText ?? value ?? 0}
      </div>
    </div>
  );
}

function SetupItem({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
      {ok ? (
        <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
      ) : (
        <AlertCircle className="h-4 w-4 shrink-0 text-amber-500" />
      )}
      <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
    </div>
  );
}

function BarRow({ label, count, max }: { label: string; count: number; max: number }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div>
      <div className="mb-1 flex justify-between text-xs text-gray-600 dark:text-gray-400">
        <span>{label}</span>
        <span className="tabular-nums">{count}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div className="h-full rounded-full bg-primary-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default CommunicationsDashboardPanel;
